import { useMemo, useState } from "react"
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  List as MuiList,
  ListItem,
  ListItemText,
  Typography,
} from "@mui/material"
import { WorkspacePremium } from "@mui/icons-material"
import {
  useListContext,
  useNotify,
  usePermissions,
  useRefresh,
  useTranslate,
  useUnselectAll,
} from "react-admin"
import { AxiosError } from "axios"
import { apiService } from "../../apiService"
import { InvoiceReadModel } from "../../GeneratedClients/FreeBillApi"
import { Permissions } from "../../authentication/Permissions"
import UpgradeDialog from "../../authorization/UpgradeDialog"
import DeterminateProgress from "../../customStyles/DeterminateProgress"
import {
  BookSkipReason,
  buildBookInvoiceCommand,
  canBookInvoice,
} from "../actionDialogs/bookInvoiceHelpers"

interface IProps {
  recordIds: string[]
}

type BreakdownKey = "bookable" | BookSkipReason | "notLoaded"
type Breakdown = Record<BreakdownKey, number>

type BulkErrorReason = BookSkipReason | "backendError" | "notLoaded"
type BulkError = { invoiceNumber: string; reason: BulkErrorReason; detail?: string }

const InvoiceBookBulkButton = ({ recordIds }: IProps) => {
  const translate = useTranslate()
  const notify = useNotify()
  const refresh = useRefresh()
  const unselectAll = useUnselectAll("invoices")
  const { data: listData } = useListContext()
  const invoicesApi = apiService.InvoicesApi
  const { isLoading: isLoadingPermissions, permissions } = usePermissions<Permissions>()

  const [openUpgrade, setOpenUpgrade] = useState(false)
  const [openConfirm, setOpenConfirm] = useState(false)
  const [openSummary, setOpenSummary] = useState(false)
  const [invoices, setInvoices] = useState<InvoiceReadModel[]>([])
  const [missingIds, setMissingIds] = useState<string[]>([])
  const [running, setRunning] = useState(false)
  const [done, setDone] = useState(0)
  const [successCount, setSuccessCount] = useState(0)
  const [errors, setErrors] = useState<BulkError[]>([])

  const bookingPeriod = localStorage.getItem("bookingPeriod")

  const totalSelected = invoices.length + missingIds.length

  const breakdown: Breakdown | null = useMemo(() => {
    if (totalSelected === 0) return null
    const counts: Breakdown = {
      bookable: 0,
      alreadyBooked: 0,
      saleCarInvoice: 0,
      invalidPeriod: 0,
      invalidItems: 0,
      notLoaded: missingIds.length,
    }
    for (const invoice of invoices) {
      const check = canBookInvoice(invoice)
      if (check.ok) counts.bookable++
      else counts[check.reason]++
    }
    return counts
  }, [invoices, missingIds, totalSelected])

  const handleClick = () => {
    if (isLoadingPermissions) return
    if (!permissions?.fffUser) {
      setOpenUpgrade(true)
      return
    }
    if (recordIds.length === 0) return
    const byId = new Map(((listData ?? []) as InvoiceReadModel[]).map(r => [r.id as string, r]))
    const loaded: InvoiceReadModel[] = []
    const missing: string[] = []
    for (const id of recordIds) {
      const invoice = byId.get(id)
      if (invoice) loaded.push(invoice)
      else missing.push(id)
    }
    if (loaded.length === 0 && missing.length === recordIds.length) {
      notify(translate("resources.invoices.bulkActions.book.loadError"), { type: "error" })
      return
    }
    setInvoices(loaded)
    setMissingIds(missing)
    setOpenConfirm(true)
  }

  const handleConfirm = async () => {
    setRunning(true)
    setDone(0)
    setSuccessCount(0)
    setErrors([])
    let localDone = 0
    let localSuccess = 0
    const localErrors: BulkError[] = []
    for (const id of missingIds) {
      localErrors.push({ invoiceNumber: id, reason: "notLoaded" })
      localDone++
      setDone(localDone)
    }
    for (const invoice of invoices) {
      const invoiceNumber = invoice.invoiceNumber ?? ""
      const check = canBookInvoice(invoice)
      if (!check.ok) {
        localErrors.push({ invoiceNumber, reason: check.reason })
      } else {
        try {
          await invoicesApi.bookInvoice({
            id: invoice.id!,
            bookInvoiceCommand: buildBookInvoiceCommand(invoice),
          })
          localSuccess++
        } catch (e) {
          const ax = e as AxiosError
          const detail = ax.response?.data ? String(ax.response.data) : ax.message
          localErrors.push({ invoiceNumber, reason: "backendError", detail })
        }
      }
      localDone++
      setDone(localDone)
    }
    setSuccessCount(localSuccess)
    setErrors(localErrors)
    setRunning(false)
    setOpenConfirm(false)
    setOpenSummary(true)
  }

  const handleConfirmClose = () => {
    if (running) return
    setOpenConfirm(false)
  }

  const handleSummaryClose = () => {
    setOpenSummary(false)
    setInvoices([])
    setMissingIds([])
    unselectAll()
    refresh()
  }

  const translateReason = (reason: BulkErrorReason) =>
    translate(`resources.invoices.bulkActions.book.reason.${reason}`)

  return (
    <>
      <Button
        size="small"
        disabled={running || recordIds.length === 0}
        onClick={handleClick}
      >
        {translate("resources.invoices.bulkActions.book.label")}
        {permissions && !permissions.fffUser && (
          <WorkspacePremium sx={{ ml: 0.5 }} fontSize="small" color="action" />
        )}
      </Button>

      <Dialog open={openConfirm} onClose={handleConfirmClose} fullWidth maxWidth="sm">
        <DialogTitle>{translate("resources.invoices.bulkActions.book.confirmTitle", { smart_count: totalSelected })}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 1 }}>
            {translate("resources.invoices.bulkActions.book.confirmBody", { smart_count: totalSelected })}
          </DialogContentText>
          {bookingPeriod && (
            <DialogContentText sx={{ mb: 1 }}>
              {translate("resources.invoices.bookingPeriod")}: <strong>{bookingPeriod}</strong>
            </DialogContentText>
          )}
          <DialogContentText sx={{ mb: 1, fontStyle: "italic" }}>
            {translate("resources.invoices.bulkActions.book.irreversibilityHint")}
          </DialogContentText>
          {breakdown && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                {translate("resources.invoices.bulkActions.book.breakdownTitle")}
              </Typography>
              <MuiList dense disablePadding>
                <ListItem disableGutters>
                  <ListItemText
                    primary={`${translate("resources.invoices.bulkActions.book.breakdown.bookable")}: ${breakdown.bookable}`}
                  />
                </ListItem>
                {breakdown.alreadyBooked > 0 && (
                  <ListItem disableGutters>
                    <ListItemText primary={`${translateReason("alreadyBooked")}: ${breakdown.alreadyBooked}`} />
                  </ListItem>
                )}
                {breakdown.saleCarInvoice > 0 && (
                  <ListItem disableGutters>
                    <ListItemText primary={`${translateReason("saleCarInvoice")}: ${breakdown.saleCarInvoice}`} />
                  </ListItem>
                )}
                {breakdown.invalidPeriod > 0 && (
                  <ListItem disableGutters>
                    <ListItemText primary={`${translateReason("invalidPeriod")}: ${breakdown.invalidPeriod}`} />
                  </ListItem>
                )}
                {breakdown.invalidItems > 0 && (
                  <ListItem disableGutters>
                    <ListItemText primary={`${translateReason("invalidItems")}: ${breakdown.invalidItems}`} />
                  </ListItem>
                )}
                {breakdown.notLoaded > 0 && (
                  <ListItem disableGutters>
                    <ListItemText primary={`${translateReason("notLoaded")}: ${breakdown.notLoaded}`} />
                  </ListItem>
                )}
              </MuiList>
            </Box>
          )}
          {running && (
            <Box sx={{ mt: 2 }}>
              <DeterminateProgress done={done} total={totalSelected} />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleConfirmClose} disabled={running}>
            {translate("ra.action.cancel")}
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={running || !breakdown || breakdown.bookable === 0}
          >
            {translate("resources.invoices.bulkActions.book.label")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openSummary} onClose={handleSummaryClose} fullWidth maxWidth="sm">
        <DialogTitle>{translate("resources.invoices.bulkActions.book.summaryTitle")}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 1 }}>
            {translate("resources.invoices.bulkActions.book.summaryBody", {
              success: successCount,
              total: totalSelected,
            })}
          </DialogContentText>
          {errors.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                {translate("resources.invoices.bulkActions.book.errorsTitle")}
              </Typography>
              <MuiList dense disablePadding>
                {errors.map((err, idx) => (
                  <ListItem key={idx} disableGutters>
                    <ListItemText
                      primary={`${err.invoiceNumber || "—"}: ${translateReason(err.reason)}`}
                      secondary={err.detail}
                    />
                  </ListItem>
                ))}
              </MuiList>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleSummaryClose}>{translate("ra.action.close")}</Button>
        </DialogActions>
      </Dialog>

      <UpgradeDialog
        open={openUpgrade}
        setOpen={setOpenUpgrade}
        title={translate("resources.authorization.upgradeDialog.title")}
        content={translate("resources.authorization.upgradeDialog.description")}
      />
    </>
  )
}

export default InvoiceBookBulkButton
